import { CaseType, ICase } from '@brands/services/cases/types/ICase';

// Care coordination is included in the cost of these clinical cases, so a care
// coordination case requested from one of them never asks the patient for
// intake or payment, whatever care coordination is priced at on the platform.
const CARE_COORDINATION_INCLUDED_SOURCE_TYPES: string[] = [
  CaseType.video_call_scheduled,
  CaseType.phone_call_scheduled,
  CaseType.one_report,
  CaseType.clinical_enablement,
  CaseType.second_opinion,
];

// The case created immediately before `actionCase` among `journeyCases`, by the
// same created_at ordering findPreviousJourneyCase uses. Ids are compared as
// strings for the same type-mismatch reason given there.
type JourneyCaseLike = { id: number | string; created_at: string; type: string };

const previousCaseIn = (actionCase: ICase, journeyCases: JourneyCaseLike[]): JourneyCaseLike | null => {
  const actionCaseId = String(actionCase.id);
  const actionCreatedAt = new Date(actionCase.created_at).getTime();
  return journeyCases
    .filter((c) => String(c.id) !== actionCaseId && new Date(c.created_at).getTime() < actionCreatedAt)
    .reduce<JourneyCaseLike | null>(
      (latest, c) => (!latest || new Date(c.created_at).getTime() > new Date(latest.created_at).getTime() ? c : latest),
      null,
    );
};

// True for a care coordination case that came from one of the clinical cases
// above, i.e. one the patient never has to complete an intake for. Only the
// case directly before it counts: that's the case the care coordination was
// requested from.
export const isIncludedCareCoordination = (actionCase: ICase, journeyCases: JourneyCaseLike[]): boolean => {
  if (actionCase.type !== CaseType.care_coordination) return false;
  const sourceCase = previousCaseIn(actionCase, journeyCases);
  return !!sourceCase && CARE_COORDINATION_INCLUDED_SOURCE_TYPES.includes(sourceCase.type);
};
